import React, { useState } from 'react';
import { 
  Text, 
  TextInput, 
  Group, 
  SegmentedControl, 
  Tooltip, 
  Loader, 
  ScrollArea, 
  Image,
  Center,
  Alert,
  Divider,
  Box,
  Button,
  ActionIcon
} from '@mantine/core';
import { 
  IconSearch, 
  IconAlertCircle, 
  IconShieldUp, 
  IconShieldOff,
  IconX
} from '@tabler/icons-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Effect, EffectWithGroup } from '../api/effects';
import { useEffects } from '../hooks/useEffects';

interface EffectsListProps {
  onEffectSelect: (effect: Effect) => void;
  selectedEffect: Effect | null;
}

/**
 * Effects list component
 * Displays a list of effects with search and filtering
 */
const EffectsList: React.FC<EffectsListProps> = ({ onEffectSelect, selectedEffect }) => {
  // State for search and filter
  const [search, setSearch] = useState('');
  const [effectType, setEffectType] = useState<'ALL' | 'BUFF' | 'DEBUFF' | 'GROUP'>('ALL');
  
  // Reference for virtual list
  const parentRef = React.useRef<HTMLDivElement>(null);
  
  // Fetch effects with search and filters
  const { 
    effects, 
    isLoading, 
    isError, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage,
    pagination
  } = useEffects({
    search,
    type: effectType === 'BUFF' || effectType === 'DEBUFF' ? effectType : undefined,
  });
  
  // Фильтрация по типу GROUP
  const filteredEffects = effects.filter(effect => {
    if (effectType === 'GROUP') return (effect as any).type === 'GROUP';
    if (effectType === 'BUFF' || effectType === 'DEBUFF') return !(effect as any).type && (effect as Effect).is_harmful === (effectType === 'BUFF' ? '0' : '1');
    return !(effect as any).type; // Обычные эффекты и группы только при ALL
  });
  
  // Setup virtualizer for efficient rendering
  const rowVirtualizer = useVirtualizer({
    count: filteredEffects.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // Adjusted for new layout
    overscan: 5,
  });
  
  // Handle infinite scrolling
  const lastVirtualItem = rowVirtualizer.getVirtualItems().at(-1);
  
  React.useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    
    if (
      lastVirtualItem &&
      lastVirtualItem.index >= filteredEffects.length - 1 - rowVirtualizer.options.overscan
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage, 
    isFetchingNextPage, 
    lastVirtualItem, 
    filteredEffects.length,
    fetchNextPage,
    rowVirtualizer.options.overscan
  ]);
  
  // Get the icon URL for an effect
  const getEffectIconUrl = (iconFileName: string) => {
    // Convert filename to lowercase to make it case-insensitive
    return `/icons/effects/${iconFileName.toLowerCase()}.jpg`;
  };
  
  // Helper function to determine if an effect is beneficial based on is_harmful flag
  const isEffectBeneficial = (effect: Effect): boolean => {
    // Use comparison with "0" since is_harmful is a string
    return effect.is_harmful === "0";
  };
  
  // Render effect item with tooltip
  const renderEffectItem = (effect: EffectWithGroup, isSelected: boolean) => {
    if ((effect as any).type === 'GROUP') {
      // Групповой эффект
      return (
        <Tooltip
          label={<div dangerouslySetInnerHTML={{ __html: effect.tooltip }} />} 
          position="right"
          withArrow
          multiline
          w={220}
        >
          <div>
            <div 
              className={`p-2 flex items-center cursor-pointer transition-colors ${
                isSelected 
                  ? 'bg-blue-100' 
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => onEffectSelect(effect as any)}
            >
              <div className="w-10 h-10 mr-4 flex-shrink-0">
                <Image
                  src={getEffectIconUrl(effect.icon_file_name)}
                  alt={effect.name}
                  fallbackSrc="/icons/fallback.jpg"
                  fit="contain"
                  className="rounded"
                />
              </div>
              <div className="flex-grow flex items-center">
                <Text size="sm" fw={isSelected ? 700 : 500} lineClamp={1} style={{ marginLeft: '8px' }} >
                  {effect.name}
                </Text>
                <Text size="xs" c="blue" fw={700} ml={8}>
                  Group
                </Text>
              </div>
            </div>
            <Divider my={0} opacity={0.2} />
          </div>
        </Tooltip>
      );
    } else {
      // Обычный эффект
      const isBeneficial = isEffectBeneficial(effect as any);
      return (
        <Tooltip
          label={<div dangerouslySetInnerHTML={{ __html: effect.tooltip }} />} 
          position="right"
          withArrow
          multiline
          w={220}
        >
          <div>
            <div 
              className={`p-2 flex items-center cursor-pointer transition-colors ${
                isSelected 
                  ? 'bg-blue-100' 
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => onEffectSelect(effect as any)}
            >
              <div className="w-10 h-10 mr-4 flex-shrink-0">
                <Image
                  src={getEffectIconUrl(effect.icon_file_name)}
                  alt={effect.name}
                  fallbackSrc="/icons/fallback.jpg"
                  fit="contain"
                  className="rounded"
                />
              </div>
              <div className="flex-grow flex items-center">
                <Text size="sm" fw={isSelected ? 700 : 500} lineClamp={1} style={{ marginLeft: '8px' }} >
                  {effect.name}
                </Text>
                <div className="mr-2 flex-shrink-0" style={{ marginLeft: '4px' }} >
                  {isBeneficial ? 
                    <IconShieldOff size={14} color="green" stroke={1.5} /> : 
                    <IconShieldUp size={14} color="red" stroke={1.5} />
                  }
                </div>
              </div>
            </div>
            <Divider my={0} opacity={0.2} />
          </div>
        </Tooltip>
      );
    }
  };
  
  return (
    <div className="h-full flex flex-col px-2">
      
      {/* Search and filters */}
      <Group mb="md" align="center">
        <TextInput
          placeholder="Search effects..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          rightSection={
            search ? (
              <ActionIcon onClick={() => setSearch('')} size="sm" variant="subtle">
                <IconX size={16} />
              </ActionIcon>
            ) : null
          }
          style={{ flex: 1 }}
          size="sm"
        />
        
        <SegmentedControl
          value={effectType}
          onChange={(value) => setEffectType(value as 'ALL' | 'BUFF' | 'DEBUFF' | 'GROUP')}
          data={[
            { label: 'All', value: 'ALL' },
            { label: 'Buffs', value: 'BUFF' },
            { label: 'Debuffs', value: 'DEBUFF' },
            { label: 'Groups', value: 'GROUP' },
          ]}
          size="xs"
          radius="xl"
          color={effectType === 'BUFF' ? 'green' : effectType === 'DEBUFF' ? 'red' : 'blue'}
        />
      </Group>
      
      {/* Effects list */}
      <div className="flex-grow overflow-hidden rounded-md">
        <ScrollArea className="h-full" viewportRef={parentRef}>
          {isLoading && !filteredEffects.length ? (
            <Center style={{ height: '100%' }}>
              <Loader size="md" />
            </Center>
          ) : isError ? (
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
              Failed to load effects
            </Alert>
          ) : filteredEffects.length === 0 ? (
            <Center style={{ height: '100%' }}>
              <Text c="dimmed">No effects found</Text>
            </Center>
          ) : (
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                const effect = filteredEffects[virtualItem.index];
                const isSelected = selectedEffect?.name === effect.name;
                
                return (
                  <div
                    key={virtualItem.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    {renderEffectItem(effect, isSelected)}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>
      
      {/* Load more button */}
      {hasNextPage && (
        <Box mt="sm" className="flex justify-center">
          <Button
            size="xs"
            variant="light"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
            disabled={isFetchingNextPage}
          >
            Load more ({pagination.loadedItems} of {pagination.totalItems})
          </Button>
        </Box>
      )}
    </div>
  );
};

export default EffectsList; 